import { useDebouncedEffect } from '@react-hookz/web'
import { Item } from '@react-stately/collections'
import { ComboBox } from 'components/ComboBox/ComboBox'
import { useState, useRef, useEffect } from 'react'
import Styles from './AddLocationPill.module.scss'
import { Button } from 'blaze-ui/Button'
import { Plus } from 'blaze-ui/icons'
import { setOptions, importLibrary } from '@googlemaps/js-api-loader'
import { REACT_APP_GOOGLE_MAPS_API_KEY } from 'env'

type Cache = Record<string, Array<string>>

interface Props {
  isActive?: boolean
  onActiveChange: (active: boolean) => any
  onSelect: (value: string) => any
  locations?: Set<string>
}

export const AddLocationPill = ({ isActive = false, onActiveChange, onSelect, locations = new Set() }: Props) => {
  const [query, setQuery] = useState('')
  const [results, setResults] = useState<Array<string>>([])

  const cache = useRef<Cache>({})

  useEffect(() => {
    setOptions({ key: REACT_APP_GOOGLE_MAPS_API_KEY })
  }, [])

  useDebouncedEffect(
    async () => {
      if (query.trim() === '') {
        setResults([])
        return
      }
      if (!cache.current[query]) {
        cache.current[query] ||= []
        const { AutocompleteSuggestion } = (await importLibrary('places')) as google.maps.PlacesLibrary
        const { suggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input: query,
          includedPrimaryTypes: ['(regions)'],
        })
        for (const suggestion of suggestions) {
          const placePrediction = suggestion.placePrediction
          if (!placePrediction) {
            continue
          }
          cache.current[query].push(placePrediction.text.toString())
        }
      }
      setResults(cache.current[query])
    },
    [query],
    200,
  )

  if (!isActive) {
    return (
      <Button aria-label="Add" variant="ghost-tertiary" frontIcon={Plus} onPress={() => onActiveChange(true)}>
        Add
      </Button>
    )
  }

  return (
    <ComboBox
      aria-label="Search location"
      autoFocus
      onBlur={() => onActiveChange(false)}
      onSelectionChange={(key) => {
        setQuery('')
        onSelect(key as string)
      }}
      placeholder="Search..."
      inputValue={query}
      onInputChange={(value) => {
        setQuery(value)
      }}
      defaultFilter={() => true}
      size="sm"
      contentsClassName={Styles.contents}
      chevronSize={0}
      popoverClassName={Styles.popover}
      inputClassName={Styles.input}
      isVirtualized={false}
      defaultItems={results.filter((result) => !locations.has(result)).map((result) => ({ id: result, name: result }))}
      noResultsMessage={<></>}
    >
      {(item) => <Item>{item.name}</Item>}
    </ComboBox>
  )
}
